/**
 * Utility functions to check if Figma, Google Docs, and Google Drive links are publicly accessible
 * Directly checks Figma API, Google Docs API, and Google Drive thumbnail API for accessibility
 */

import { remark } from 'remark';
import { visit } from 'unist-util-visit';

/**
 * Extracts all links from markdown text
 */
export const extractLinksFromMarkdown = (markdown: string): string[] => {
    if (!markdown) return [];

    const links: string[] = [];

    try {
        const tree = remark.parse(markdown);

        visit(tree, 'link', (node: any) => {
            if (node.url) {
                links.push(node.url);
            }
        });

        visit(tree, 'text', (node: any) => {
            if (node.value) {
                const urlRegex = /https?:\/\/[^\s<>"]+/g;
                const matches = node.value.match(urlRegex);
                if (matches) {
                    links.push(...matches);
                }
            }
        });

        // Remove duplicates
        return [...new Set(links)];
    } catch (error) {
        console.warn('Error parsing markdown:', error);
        // Fallback to simple regex if remark fails
        return extractLinksWithRegex(markdown);
    }
};

/**
 * Fallback regex-based extraction (used if remark fails)
 */
const extractLinksWithRegex = (markdown: string): string[] => {
    const links: string[] = [];

    // Extract markdown links: [text](url)
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    let match;
    while ((match = markdownLinkRegex.exec(markdown)) !== null) {
        links.push(match[2]);
    }

    // Remove markdown link syntax from text to avoid duplicates
    const textWithoutMarkdownLinks = markdown.replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        ''
    );

    // Extract bare URLs
    const urlRegex = /https?:\/\/[^\s<>"]+/g;
    const urlMatches = textWithoutMarkdownLinks.match(urlRegex);
    if (urlMatches) {
        links.push(...urlMatches);
    }

    return [...new Set(links)];
};

const isHostnameMatch = (url: string, patterns: string[]): boolean => {
    try {
        const urlObj = new URL(url);
        return patterns.some((pattern) => urlObj.hostname.includes(pattern));
    } catch {
        return false;
    }
};

export const isFigmaLink = (url: string): boolean => {
    return isHostnameMatch(url, ['figma.com']);
};

export const isGoogleDocsLink = (url: string): boolean => {
    return isHostnameMatch(url, ['docs.google.com']);
};

export const isGoogleDriveLink = (url: string): boolean => {
    return (
        isHostnameMatch(url, ['drive.google.com']) && url.includes('/file/d/')
    );
};

/**
 * Filters links to only include Figma, Google Docs, and Google Drive links
 */
export const filterRelevantLinks = (links: string[]): string[] => {
    return links.filter(
        (link) =>
            isFigmaLink(link) ||
            isGoogleDocsLink(link) ||
            isGoogleDriveLink(link)
    );
};

/**
 * Checks if a Figma link is publicly accessible using Figma's oEmbed API.
 * The oEmbed endpoint is CORS-enabled and requires no authentication.
 * It returns 200 for publicly shared files and 403 for private ones.
 */
const checkFigmaAccessibility = async (url: string): Promise<boolean> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
        const oEmbedUrl = `https://www.figma.com/api/oembed?url=${encodeURIComponent(
            url
        )}`;
        const response = await fetch(oEmbedUrl, {
            method: 'GET',
            credentials: 'omit',
            signal: controller.signal
        });

        return response.ok;
    } catch (error: any) {
        if (error.name === 'AbortError') {
            console.warn(`Timeout checking Figma URL: ${url}`);
            return false;
        }
        console.warn(`Error checking Figma accessibility for ${url}:`, error);
        return false;
    } finally {
        clearTimeout(timeoutId);
    }
};

/**
 * Checks if a Google Drive file (drive.google.com/file/d/…) is publicly accessible.
 * Uses the thumbnail endpoint which returns 200 for public files and 403 for private
 * ones, and is CORS-friendly — no URL mangling needed.
 */
const checkGoogleDriveAccessibility = async (url: string): Promise<boolean> => {
    const fileIdMatch = url.match(/\/file\/d\/([a-zA-Z0-9-_]+)/);
    if (!fileIdMatch) return false;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    try {
        const fileId = fileIdMatch[1];
        const thumbnailUrl = `https://drive.google.com/thumbnail?id=${fileId}&sz=w1`;
        const response = await fetch(thumbnailUrl, {
            method: 'GET',
            credentials: 'omit',
            signal: controller.signal
        });

        return response.ok;
    } catch (error: any) {
        if (error.name === 'AbortError') {
            console.warn(`Timeout checking Google Drive URL: ${url}`);
            return false;
        }
        console.warn(
            `Error checking Google Drive accessibility for ${url}:`,
            error
        );
        return false;
    } finally {
        clearTimeout(timeoutId);
    }
};

/**
 * Checks if a Google Docs link is publicly accessible.
 * Tries to access the document without authentication.
 */
const checkGoogleDocsAccessibility = async (url: string): Promise<boolean> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    try {
        // Convert edit links to preview/pub links for better public access checking
        let checkUrl = url;
        if (url.includes('/edit')) {
            checkUrl = url.replace('/edit', '/preview');
        } else if (url.includes('/d/') && !url.includes('/pub')) {
            // Try the publish URL format which is more reliably public
            const docIdMatch = url.match(/\/d\/([a-zA-Z0-9-_]+)/);
            if (docIdMatch) {
                const docId = docIdMatch[1];
                checkUrl = `https://docs.google.com/document/d/${docId}/pub`;
            }
        }

        const response = await fetch(checkUrl, {
            method: 'GET',
            credentials: 'omit',
            signal: controller.signal,
            redirect: 'manual'
        });

        // Check if redirected to login page
        if (
            response.type === 'opaqueredirect' ||
            response.status === 302 ||
            response.status === 301
        ) {
            const location = response.headers.get('location');
            if (
                location &&
                (location.includes('accounts.google.com') ||
                    location.includes('login'))
            ) {
                return false;
            }
        }

        // Check for permission errors
        if (response.status === 401 || response.status === 403) {
            return false;
        }

        // If successful, check the content
        if (response.ok || response.status === 200) {
            try {
                const text = await response.text();
                const lowerText = text.toLowerCase();

                // Check for access restriction indicators
                if (
                    lowerText.includes('you need permission') ||
                    lowerText.includes('request access') ||
                    lowerText.includes('sign in to continue') ||
                    lowerText.includes('restricted') ||
                    lowerText.includes('private')
                ) {
                    return false;
                }

                // If we can see document content, it's likely public
                if (
                    lowerText.includes('docs-title') ||
                    lowerText.includes('document-content') ||
                    text.includes('ViewerContainer')
                ) {
                    return true;
                }
            } catch (e) {
                console.warn(
                    `Could not parse Google Docs response for ${url}:`,
                    e
                );
            }
        }

        return response.ok;
    } catch (error: any) {
        if (error.name === 'AbortError') {
            console.warn(`Timeout checking Google Docs URL: ${url}`);
            return false;
        }
        console.warn(
            `Error checking Google Docs accessibility for ${url}:`,
            error
        );
        return false;
    } finally {
        clearTimeout(timeoutId);
    }
};

/**
 * Checks if a single link is publicly accessible.
 * Routes to the appropriate checker based on the link type.
 */
export const checkLinkAccessibility = async (url: string): Promise<boolean> => {
    if (isFigmaLink(url)) {
        return await checkFigmaAccessibility(url);
    } else if (isGoogleDriveLink(url)) {
        return await checkGoogleDriveAccessibility(url);
    } else if (isGoogleDocsLink(url)) {
        return await checkGoogleDocsAccessibility(url);
    }
    return true;
};

/**
 * Checks if all Figma and Google Docs links in the description are publicly accessible
 * Returns an object with:
 * - allAccessible: boolean indicating if all links are accessible
 * - inaccessibleLinks: array of links that are not accessible
 */
export const checkAllLinksAccessibility = async (
    description: string
): Promise<{ allAccessible: boolean; inaccessibleLinks: string[] }> => {
    const allLinks = extractLinksFromMarkdown(description);
    const relevantLinks = filterRelevantLinks(allLinks);

    if (relevantLinks.length === 0) {
        return { allAccessible: true, inaccessibleLinks: [] };
    }

    const results = await Promise.all(
        relevantLinks.map(async (link) => ({
            link,
            accessible: await checkLinkAccessibility(link)
        }))
    );

    const inaccessibleLinks = results
        .filter((result) => !result.accessible)
        .map((result) => result.link);

    return {
        allAccessible: inaccessibleLinks.length === 0,
        inaccessibleLinks
    };
};
